import { ReactElement, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useRunAuxSensorData } from 'hooks/useRuns';
import { useStoredState } from 'hooks/useStoredState';
import { getWheelCircumference } from 'utils/channelNamesGridded';
import { storedNumber, storedOneOf } from 'utils/storedValue';
import {
  AUX_TIME_OFFSET_LIMIT_S,
  MOVING_AVERAGE_WINDOWS,
  POWER_PLOT,
  POWER_SMOOTH_CHANNELS,
  SPEED_SMOOTH_CHANNELS,
  WIND_PLOT,
  SpeedMode,
  DataMode,
  buildPowerSeries,
  buildSpeedSeries,
  buildComparisonScatter,
  buildPowerComparisonScatter,
  buildTraverseSegments,
  gridIntervalS,
  hasTraverseData,
  maxWheelSpeedMs,
  smoothChannels,
  speedUnit,
  timeOffsetToSamples,
} from './auxSensorChannels';
import {
  AuxTimeOffsetControl,
  MinSpeedControl,
  ModeRadio,
  MovingAverageControl,
} from './AuxControls';
import AuxSensorPlot from './AuxSensorPlot';
import AuxComparisonPlot from './AuxComparisonPlot';
import AuxRatioForcePlot from './AuxRatioForcePlot';
import AuxRatioTimePlot from './AuxRatioTimePlot';
import CollapsibleSection from './CollapsibleSection';

interface AuxSensorContext {
  run?: { id?: string } | null;
  calibration?: any;
}

const DEFAULT_SPEED_THRESHOLD_MS = 2;

/** Fallback offset step (s) when the grid interval can't be read off the data. */
const FALLBACK_GRID_INTERVAL_S = 0.25;

const SPEED_MODE_OPTS = [
  { value: 'rotational', label: 'Rotational' },
  { value: 'linear', label: 'Linear' },
];
const DATA_MODE_OPTS = [
  { value: 'all', label: 'All' },
  { value: 'traverse', label: 'Traverse' },
];

/** Fallback upper bound for the min-speed slider when the run has no wheel data. */
const SPEED_SLIDER_FALLBACK_MAX = 15;

/**
 * localStorage keys for this tab's controls, so a rider's chosen view carries
 * across runs. Run-dependent values (the min-speed threshold, the fit windows)
 * are stored raw and clamped against the loaded run's range on read — see
 * `useStoredState`.
 */
const STORE = {
  speedMode: 'runAuxSensor.speedMode',
  dataMode: 'runAuxSensor.dataMode',
  speedThreshold: 'runAuxSensor.speedThreshold',
  powerOffset: 'runAuxSensor.powerOffsetS',
  powerAvg: 'runAuxSensor.powerAvgWindow',
  speedAvg: 'runAuxSensor.speedAvgWindow',
};

const SPEED_MODES: SpeedMode[] = ['rotational', 'linear'];
const DATA_MODES: DataMode[] = ['all', 'traverse'];
const AVG_WINDOWS = [...MOVING_AVERAGE_WINDOWS];

export default function RunAuxSensor(): ReactElement {
  const { runId: runIdParam } = useParams<{ runId: string }>();
  const { run, calibration } = useOutletContext<AuxSensorContext>();
  const runId = runIdParam ?? run?.id ?? null;

  const [speedMode, setSpeedMode] = useStoredState<SpeedMode>(
    STORE.speedMode, 'rotational', storedOneOf(SPEED_MODES),
  );
  const [storedDataMode, setDataMode] = useStoredState<DataMode>(
    STORE.dataMode, 'all', storedOneOf(DATA_MODES),
  );
  const [storedSpeedThreshold, setSpeedThreshold] = useStoredState(
    STORE.speedThreshold, DEFAULT_SPEED_THRESHOLD_MS, storedNumber(0),
  );
  const [powerOffsetRequestS, setPowerOffsetRequestS] = useStoredState(
    STORE.powerOffset, 0, storedNumber(-AUX_TIME_OFFSET_LIMIT_S, AUX_TIME_OFFSET_LIMIT_S),
  );
  const [powerAvgWindow, setPowerAvgWindow] = useStoredState(
    STORE.powerAvg, 0, storedOneOf(AVG_WINDOWS),
  );
  const [speedAvgWindow, setSpeedAvgWindow] = useStoredState(
    STORE.speedAvg, 0, storedOneOf(AVG_WINDOWS),
  );

  const { data: auxRun, isPending } = useRunAuxSensorData(runId);
  const gridded = auxRun?.data_gridded as Record<string, (number | null)[]> | undefined;
  const circumference = getWheelCircumference(calibration);

  const hasTraverse = useMemo(() => hasTraverseData(gridded), [gridded]);
  const traverseSegments = useMemo(() => buildTraverseSegments(gridded), [gridded]);

  // A stored 'traverse' selection is meaningless on a run without traverse data —
  // its toggle isn't even rendered — and would mask every plot to empty.
  const dataMode: DataMode = storedDataMode === 'traverse' && !hasTraverse ? 'all' : storedDataMode;
  const segmentsForPlots = dataMode === 'traverse' ? traverseSegments : undefined;

  // The scatter can only re-pair samples by index, so the requested offset is
  // snapped to whole grid samples and the time plot uses the snapped value too.
  const gridInterval = useMemo(() => gridIntervalS(gridded) || FALLBACK_GRID_INTERVAL_S, [gridded]);
  const powerOffsetSamples = timeOffsetToSamples(powerOffsetRequestS, gridInterval);
  const powerOffsetS = powerOffsetSamples * gridInterval;

  // `smoothChannels` only rewrites the channels it is handed, and the two groups
  // are disjoint, so one dict with both averages applied is the correct input for
  // every plot on the tab — no per-plot choice of which variant to pass.
  const smoothed = useMemo(
    () => smoothChannels(
      smoothChannels(gridded, POWER_SMOOTH_CHANNELS, powerAvgWindow),
      SPEED_SMOOTH_CHANNELS,
      speedAvgWindow,
    ),
    [gridded, powerAvgWindow, speedAvgWindow],
  );

  const powerSeries = useMemo(
    () => buildPowerSeries(smoothed, dataMode, powerOffsetS),
    [smoothed, dataMode, powerOffsetS],
  );
  const speedSeries = useMemo(
    () => buildSpeedSeries(smoothed, speedMode, circumference, dataMode),
    [smoothed, speedMode, circumference, dataMode],
  );
  const powerComparison = useMemo(
    () => buildPowerComparisonScatter(smoothed, dataMode, powerOffsetSamples),
    [smoothed, dataMode, powerOffsetSamples],
  );
  const comparison = useMemo(
    () => buildComparisonScatter(smoothed, circumference, dataMode),
    [smoothed, circumference, dataMode],
  );
  // Deliberately off the raw grid so the slider's range doesn't move with smoothing.
  const maxSpeedMs = useMemo(() => maxWheelSpeedMs(gridded, circumference), [gridded, circumference]);
  // A threshold carried over from a faster run would sit past the end of this
  // run's slider, so pin it to the maximum. Clamped on read, not on write, so the
  // stored preference still applies in full to the next run that can reach it.
  const speedSliderMax = maxSpeedMs || SPEED_SLIDER_FALLBACK_MAX;
  const speedThreshold = Math.min(storedSpeedThreshold, speedSliderMax);

  const dataModeRadio = (name: string) =>
    hasTraverse ? (
      <ModeRadio name={name} value={dataMode} options={DATA_MODE_OPTS} onChange={(v) => setDataMode(v as DataMode)} />
    ) : null;

  // Only meaningful once there is a second power meter to move.
  const hasAuxPower = powerSeries.length > 1;

  const powerOffsetNote = powerOffsetS !== 0
    ? `Auxiliary meter timestamps shifted by ${powerOffsetS > 0 ? '+' : ''}${powerOffsetS.toFixed(2)} s `
      + `(${powerOffsetSamples > 0 ? '+' : ''}${powerOffsetSamples} grid samples).`
    : undefined;

  if (!runId) {
    return <div className="cr-p-4 cr-text-gray-400">No run selected.</div>;
  }

  return (
    <div className="cr-space-y-6">
      <AuxSensorPlot
        title={POWER_PLOT.title}
        unit={POWER_PLOT.unit}
        series={powerSeries}
        isLoading={isPending}
        headerExtra={dataModeRadio('auxDataModePower')}
        controls={(
          <>
            <MovingAverageControl
              name="auxPowerMovingAvg"
              window={powerAvgWindow}
              onChange={setPowerAvgWindow}
              intervalS={gridInterval}
            />
            {hasAuxPower ? (
              <AuxTimeOffsetControl
                requestS={powerOffsetRequestS}
                onChange={setPowerOffsetRequestS}
                appliedS={powerOffsetS}
                stepS={gridInterval}
              />
            ) : null}
          </>
        )}
        traverseSegments={segmentsForPlots}
      />

      <CollapsibleSection title="Power Comparison" isLoading={isPending} spinnerLabel="Loading comparison…">
        <AuxComparisonPlot
          title="Power Meter Comparison"
          storageKey="powerComparison"
          xLabel="Power 1 (W)"
          yLabel="Power 2 (W)"
          unit="W"
          series={powerComparison.series}
          axisMax={powerComparison.axisMax}
          fit={{ sliderLabel: 'Power window', sliderStep: 5, manualOffset: true }}
          note={powerOffsetNote}
        />
      </CollapsibleSection>

      <AuxSensorPlot
        title="Wheel Speed"
        unit={speedUnit(speedMode)}
        series={speedSeries}
        isLoading={isPending}
        headerExtra={
          <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-6">
            <ModeRadio
              name="auxSpeedMode"
              value={speedMode}
              options={SPEED_MODE_OPTS}
              onChange={(v) => setSpeedMode(v as SpeedMode)}
            />
            {dataModeRadio('auxDataModeSpeed')}
          </div>
        }
        controls={(
          <MovingAverageControl
            name="auxSpeedMovingAvg"
            window={speedAvgWindow}
            onChange={setSpeedAvgWindow}
            intervalS={gridInterval}
          />
        )}
        traverseSegments={segmentsForPlots}
      />

      <CollapsibleSection title="Sensor Comparison" isLoading={isPending} spinnerLabel="Loading comparison…">
        <AuxRatioTimePlot
          gridded={smoothed}
          circumference={circumference}
          threshold={speedThreshold}
          dataMode={dataMode}
        />
        <MinSpeedControl value={speedThreshold} onChange={setSpeedThreshold} maxMs={speedSliderMax} />
        <div className="cr-grid cr-grid-cols-1 cr-gap-6 xl:cr-grid-cols-2">
          <AuxComparisonPlot
            title="Wheel Speed Sensor Comparison"
            storageKey="wheelSpeedComparison"
            xLabel="Wheel Speed 1 (km/h)"
            yLabel="Wheel Speed 2 / GPS (km/h)"
            unit="km/h"
            series={comparison.series}
            axisMax={comparison.axisMax}
          />
          <AuxRatioForcePlot
            gridded={smoothed}
            circumference={circumference}
            threshold={speedThreshold}
            dataMode={dataMode}
          />
        </div>
      </CollapsibleSection>

      <AuxSensorPlot title={WIND_PLOT.title} unit={WIND_PLOT.unit} series={[]} inactive />
    </div>
  );
}
